import { HttpErrorResponse } from '@angular/common/http';
import { isString, mapValues } from 'lodash';

export class ErrorModel {

  public readonly timestamp?: Date;
  public readonly status?: number;
  public readonly message: string;
  public readonly path?: string;
  public readonly errors?: Record<string, ValidationErrorModel>;
  public readonly externalApiError?: ExternalApiErrorModel;
  public readonly errorCode?: string;

  constructor(jsonObject: any) {
    this.timestamp = this.tryInstantiatingDate(jsonObject.timestamp);
    this.status = jsonObject.status;
    this.message = jsonObject.message;
    this.path = jsonObject.path ?? jsonObject.url;
    this.errorCode = jsonObject.errorCode;
    if (jsonObject.errors && Object.keys(jsonObject.errors).length) {
      this.errors = mapValues(jsonObject.errors, jsonValidationError => new ValidationErrorModel(jsonValidationError));
    }
    this.externalApiError = jsonObject.externalApiError && new ExternalApiErrorModel(jsonObject.externalApiError);
  }

  public static fromErrorResponse(caught: HttpErrorResponse): ErrorModel {
    let errorModel: ErrorModel;
    if (caught.error instanceof Event) {
      errorModel = new ErrorModel(caught);
    } else if (typeof caught.error === 'string') {
      errorModel = new ErrorModel(this.tryParsingErrorBody(caught.error));
    } else {
      try {
        errorModel = new ErrorModel(caught.error);
      } catch (e) {
        errorModel = new ErrorModel({ message: caught.message });
      }
    }
    return errorModel;
  }

  /**
   * Use for testing only.
   */
  public static dummy(validationPaths?: string[]): ErrorModel {
    return new ErrorModel({
      timestamp: Date.now(),
      status: 0,
      message: 'Dummy error',
      errors: validationPaths ?
        validationPaths
          .map(path => ({ path, message: 'Dummy error' }))
          .reduce((acc, { path, message }) => ({ ...acc, [path]: { message } }), {})
        : undefined
    });
  }

  public static ofMessage(message: string): ErrorModel {
    return new ErrorModel({
      timestamp: Date.now(),
      status: 0,
      message
    });
  }

  private static tryParsingErrorBody(body: string): any {
    try {
      return JSON.parse(body);
    } catch (e) {
      return { message: body };
    }
  }

  private tryInstantiatingDate(input: unknown): Date | undefined {
    let date: Date | undefined;
    if (typeof input === 'string' || typeof input === 'number') {
      date = new Date(input);
    }
    return isNaN(date?.valueOf() ?? NaN) ? undefined : date;
  }

}

export class ValidationErrorModel {
  message: string;

  constructor(jsonObject: any) {
    this.message = jsonObject.message;
  }
}

export class ExternalApiErrorModel {
  message?: string;
  status?: number;

  constructor(jsonObject: any) {
    this.message = isString(jsonObject) ? jsonObject : jsonObject.message;
    this.status = numberValue(jsonObject.status);
  }
}

function numberValue(value: any): number | undefined {
  if (typeof value === 'number' || value instanceof Number) {
    return +value;
  }
  return undefined;
}

